"use-strict";
// Copyright 2019 Google LLC.
// SPDX-License-Identifier: Apache-2.0

// Don’t send `SameSite=None` to known incompatible clients.
export default function shouldSendSameSiteNone(useragent) {
  return !isSameSiteNoneIncompatible(useragent);
}

// Classes of browsers known to be incompatible.

function isSameSiteNoneIncompatible(useragent) {
  return (
    hasWebKitSameSiteBug(useragent) ||
    dropsUnrecognizedSameSiteCookies(useragent)
  );
}

function hasWebKitSameSiteBug(useragent) {
  return (
    isIosVersion(12, useragent) ||
    (isMacosxVersion(10, 14, useragent) &&
      (isSafari(useragent) || isMacEmbeddedBrowser(useragent)))
  );
}

function dropsUnrecognizedSameSiteCookies(useragent) {
  if (isUcBrowser(useragent)) {
    return !isUcBrowserVersionAtLeast(12, 13, 2, useragent);
  }
  return (
    isChromiumBased(useragent) &&
    isChromiumVersionAtLeast(51, useragent) &&
    !isChromiumVersionAtLeast(67, useragent)
  );
}

// Match
function match(string, regex, position = 0) {
  const matches = string.match(regex);
  try {
    return matches[position];
  } catch (_) {
    return 0;
  }
}
function contains(string, regex) {
  return match(string, regex, 0) !== 0;
}

// Regex parsing of User-Agent string. (See note above!)
function isIosVersion(major, useragent) {
  const regex = /\(iP.+; CPU .*OS (\d+)[_\d]*.*\) AppleWebKit\//;
  // Extract digits from first capturing group.
  return parseInt(match(useragent, regex, 1), 10) === major;
}

function isMacosxVersion(major, minor, useragent) {
  const regex = /\(Macintosh;.*Mac OS X (\d+)_(\d+)[_\d]*.*\) AppleWebKit\//;
  // Extract digits from first and second capturing groups.
  return (
    parseInt(match(useragent, regex, 1), 10) === major &&
    parseInt(match(useragent, regex, 2), 10) === minor
  );
}

function isSafari(useragent) {
  const safari_regex = /Version\/.* Safari\//;
  return contains(useragent, safari_regex) && !isChromiumBased(useragent);
}

function isMacEmbeddedBrowser(useragent) {
  const regex = /^Mozilla\/[.\d]+ \(Macintosh;.*Mac OS X [_\d]+\) AppleWebKit\/[.\d]+ \(KHTML, like Gecko\)$/;
  return contains(useragent, regex);
}

function isChromiumBased(useragent) {
  const regex = /Chrom(e|ium)/;
  return contains(useragent, regex);
}

function isChromiumVersionAtLeast(major, useragent) {
  const regex = /Chrom[^ /]+\/(\d+)[.\d]* /;
  // Extract digits from first capturing group.
  const version = parseInt(match(useragent, regex, 1), 10);
  return version >= major;
}

function isUcBrowser(useragent) {
  const regex = /UCBrowser\//;
  return contains(useragent, regex);
}

function isUcBrowserVersionAtLeast(major, minor, build, useragent) {
  const regex = /UCBrowser\/(\d+)\.(\d+)\.(\d+)[.\d]* /;
  // Extract digits from three capturing groups.
  const major_version = parseInt(match(useragent, regex, 1), 10);
  const minor_version = parseInt(match(useragent, regex, 2), 10);
  const build_version = parseInt(match(useragent, regex, 3), 10);
  if (major_version !== major) {
    return major_version > major;
  }
  if (minor_version !== minor) {
    return minor_version > minor;
  }
  return build_version >= build;
}
